import { MEDIA_TYPES } from "./constants";

export const MEDIA_ASSET_PROPERTY_TYPES = {
  building: "Building",
  land: "Land",
};

export const MEDIA_ASSET_FILE_TYPES = {
  image: "Image",
  pdf: "PDF",
};

export const MEDIA_ASSET_PLACEHOLDER_ID = "pleaseSelect";
export const MEDIA_ASSET_TAXONOMY_STORAGE_KEY = "dpm_media_assets_taxonomy";

export const DEFAULT_MEDIA_ASSET_SLOT_IDS = ["rackingPlan", "essentialsBrochure", "parkBrochure"];
export const RESERVED_MEDIA_ASSET_SLOT_IDS = Object.values(MEDIA_TYPES);

export const DEFAULT_MEDIA_ASSET_TAXONOMY = [
  {
    id: MEDIA_ASSET_PLACEHOLDER_ID,
    name: "-Please Select-",
    propertyTypes: [MEDIA_ASSET_PROPERTY_TYPES.building, MEDIA_ASSET_PROPERTY_TYPES.land],
    fileTypes: [MEDIA_ASSET_FILE_TYPES.image, MEDIA_ASSET_FILE_TYPES.pdf],
    isSystemDefault: true,
    isPlaceholder: true,
  },
  {
    id: "rackingPlan",
    name: "Racking Plan",
    propertyTypes: [MEDIA_ASSET_PROPERTY_TYPES.building],
    fileTypes: [MEDIA_ASSET_FILE_TYPES.image, MEDIA_ASSET_FILE_TYPES.pdf],
    subTypes: ["Racking Plan"],
    isSystemDefault: true,
  },
  {
    id: "essentialsBrochure",
    name: "Essentials Brochure",
    propertyTypes: [MEDIA_ASSET_PROPERTY_TYPES.building, MEDIA_ASSET_PROPERTY_TYPES.land],
    fileTypes: [MEDIA_ASSET_FILE_TYPES.image, MEDIA_ASSET_FILE_TYPES.pdf],
    subTypes: ["Essentials Building Brochure", "Essentials Land Brochure"],
    isSystemDefault: true,
  },
  {
    id: "parkBrochure",
    name: "Park Brochure",
    propertyTypes: [MEDIA_ASSET_PROPERTY_TYPES.building, MEDIA_ASSET_PROPERTY_TYPES.land],
    fileTypes: [MEDIA_ASSET_FILE_TYPES.pdf],
    subTypes: ["Park Land Brochure", "Park Building Brochure"],
    isSystemDefault: true,
  },
];

const normalizeArray = value => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

export const toMediaAssetId = value =>
  `${value || ""}`
    .trim()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/^\w/, chr => chr.toLowerCase());

export const isReservedMediaAssetId = value => RESERVED_MEDIA_ASSET_SLOT_IDS.includes(toMediaAssetId(value));

export const normalizeMediaAssetType = asset => {
  const name = asset?.name || asset?.brochureType || "";
  const propertyTypes = normalizeArray(asset?.propertyTypes || asset?.propertyType).filter(type =>
    Object.values(MEDIA_ASSET_PROPERTY_TYPES).includes(type),
  );
  const fileTypes = normalizeArray(asset?.fileTypes || asset?.allowedAssetType).filter(type =>
    Object.values(MEDIA_ASSET_FILE_TYPES).includes(type),
  );

  return {
    id: asset?.id || toMediaAssetId(name),
    name,
    propertyTypes: propertyTypes.length ? propertyTypes : [MEDIA_ASSET_PROPERTY_TYPES.building],
    fileTypes: fileTypes.length ? fileTypes : [MEDIA_ASSET_FILE_TYPES.image, MEDIA_ASSET_FILE_TYPES.pdf],
    subTypes: normalizeArray(asset?.subTypes || asset?.subType || asset?.assetSubTypes),
    isSystemDefault: !!asset?.isSystemDefault,
    isPlaceholder: !!asset?.isPlaceholder || asset?.id === MEDIA_ASSET_PLACEHOLDER_ID,
  };
};

export const mergeWithDefaultMediaAssetTaxonomy = mediaAssets => {
  const byId = {};
  DEFAULT_MEDIA_ASSET_TAXONOMY.forEach(asset => {
    byId[asset.id] = normalizeMediaAssetType(asset);
  });
  normalizeArray(mediaAssets).forEach(asset => {
    const normalized = normalizeMediaAssetType(asset);
    if (normalized.id && normalized.name) {
      const defaultAsset = byId[normalized.id];
      byId[normalized.id] = {
        ...normalized,
        subTypes: [...new Set([...(defaultAsset?.subTypes || []), ...normalized.subTypes])],
      };
    }
  });
  return Object.values(byId);
};

export const getSelectableMediaAssetTaxonomy = mediaAssets =>
  mergeWithDefaultMediaAssetTaxonomy(mediaAssets).filter(asset => !asset.isPlaceholder);

export const getMediaAssetTaxonomy = (isEnabled = true) => {
  if (!isEnabled) return [];

  try {
    const storedTaxonomy = window.localStorage.getItem(MEDIA_ASSET_TAXONOMY_STORAGE_KEY);
    return mergeWithDefaultMediaAssetTaxonomy(storedTaxonomy ? JSON.parse(storedTaxonomy) : []);
  } catch (ex) {
    return mergeWithDefaultMediaAssetTaxonomy([]);
  }
};

export const setMediaAssetTaxonomy = mediaAssets => {
  const normalized = mergeWithDefaultMediaAssetTaxonomy(mediaAssets);
  try {
    window.localStorage.setItem(MEDIA_ASSET_TAXONOMY_STORAGE_KEY, JSON.stringify(normalized));
  } catch (ex) {
    // localStorage can be unavailable in tests/private contexts.
  }
  return normalized;
};

export const getMediaAssetPlaceholder = asset => {
  const fileTypes = normalizeArray(asset?.fileTypes);
  if (fileTypes.length === 1 && fileTypes[0] === MEDIA_ASSET_FILE_TYPES.pdf) return "Select PDF";
  if (fileTypes.length === 1 && fileTypes[0] === MEDIA_ASSET_FILE_TYPES.image) return "Select 1 Image";
  return "Select an Image or PDF";
};
